/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.Strings
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.w3c.dom.HTMLElement
import org.w3c.dom.get
import org.w3c.fetch.RequestInit
import kotlin.js.json

/**
 * Component to display personalized advice cards on the dashboard.
 * Fetches cards from the server based on user's transaction analysis.
 */
class PersonalizedAdviceCardsComponent(
    private val navigationManager: WebNavigationManager? = null,
    private val onAuthError: () -> Unit = {},
    private val onShowFilteredTransactions: ((category: String?, merchants: List<String>, title: String) -> Unit)? = null,
    private val onReviewSubscriptions: (() -> Unit)? = null
) {
    private var container: HTMLElement? = null
    private var cards: List<PersonalizedCardData> = emptyList()
    private var isLoading = true
    private var error: String? = null
    private var userDataDays: Int = 0

    // Silent auto-refresh state. When the server serves prose from an older
    // cached bundle it sets refreshing=true and regenerates in the background
    // (measured at 10-15s). We re-fetch once the job should be done and swap
    // the cards in with no spinner — the user sees the fresh copy appear.
    private var pendingRefreshTimer: Int? = null
    private var refreshRetriesLeft = MAX_SILENT_REFRESH_RETRIES

    private val json = Json { ignoreUnknownKeys = true }

    fun render(): HTMLElement {
        val element = document.createElement("div") as HTMLElement
        element.className = "personalized-advice-cards"
        container = element

        // Load cards from server
        loadPersonalizedCards()

        updateUI()
        return element
    }

    /**
     * @param isAutoRefresh true when this run was scheduled by the silent
     * background-regeneration retry. Auto runs never show the loading state and
     * never replace good cards with an error banner — they are invisible until
     * fresh data actually lands.
     */
    private fun loadPersonalizedCards(isAutoRefresh: Boolean = false) {
        if (!isAutoRefresh) {
            // A user-initiated load supersedes any queued silent refresh and
            // gets a fresh retry budget.
            cancelPendingRefresh()
            refreshRetriesLeft = MAX_SILENT_REFRESH_RETRIES
        }
        GlobalScope.launch {
            try {
                console.log("[PersonalizedAdvice] Starting to load cards...")
                if (!isAutoRefresh) {
                    isLoading = true
                    updateUI()
                }

                val token = ApiService.getSharedAuthToken()
                console.log("[PersonalizedAdvice] Token present: ${!token.isNullOrEmpty()}")
                if (token.isNullOrEmpty()) {
                    if (isAutoRefresh) return@launch
                    error = "Not authenticated"
                    isLoading = false
                    updateUI()
                    return@launch
                }

                val baseUrl = getBaseUrl()
                val lang = Strings.getLocale().code
                console.log("[PersonalizedAdvice] Fetching from: $baseUrl/api/advice/personalized?lang=$lang")
                val response = window.fetch(
                    "$baseUrl/api/advice/personalized?lang=$lang",
                    RequestInit(
                        method = "GET",
                        headers = json(
                            "Authorization" to "Bearer $token",
                            "Content-Type" to "application/json",
                            "X-Wiz3r-Tz" to browserTimezone()
                        )
                    )
                ).await()

                console.log("[PersonalizedAdvice] Response status: ${response.status}")
                if (response.status == 401.toShort()) {
                    // A silent refresh must never drive the app into the auth
                    // error path on its own — it just stops.
                    if (!isAutoRefresh) onAuthError()
                    return@launch
                }

                if (!response.ok) {
                    if (isAutoRefresh) return@launch
                    error = "Failed to load advice cards"
                    isLoading = false
                    updateUI()
                    return@launch
                }

                val responseText = response.text().await()
                console.log("[PersonalizedAdvice] Response: $responseText")
                val cardsResponse = json.decodeFromString<PersonalizedCardsApiResponse>(responseText)

                console.log("[PersonalizedAdvice] Loaded ${cardsResponse.cards.size} cards")
                cards = cardsResponse.cards
                userDataDays = cardsResponse.userDataDays
                error = null
                isLoading = false
                if (isAutoRefresh) updateUIPreservingScroll() else updateUI()

                if (cardsResponse.refreshing) {
                    scheduleSilentRefresh()
                }

            } catch (e: Exception) {
                console.error("[PersonalizedAdvice] Error: ${e.message}")
                if (isAutoRefresh) return@launch
                error = "Error loading advice"
                isLoading = false
                updateUI()
            }
        }
    }

    /**
     * Queue exactly one silent re-fetch, ~12s out (background generation is
     * 10-15s). Capped by [refreshRetriesLeft] so a server that keeps answering
     * refreshing=true can never make us poll forever.
     */
    private fun scheduleSilentRefresh() {
        if (refreshRetriesLeft <= 0) {
            console.log("[PersonalizedAdvice] Silent refresh budget exhausted, giving up")
            return
        }
        cancelPendingRefresh()
        refreshRetriesLeft--
        pendingRefreshTimer = window.setTimeout({
            pendingRefreshTimer = null
            val element = container
            // The component may have been swapped out while the timer was
            // pending — never touch a detached node.
            if (element == null || !document.body!!.contains(element)) {
                console.log("[PersonalizedAdvice] Detached, skipping silent refresh")
                return@setTimeout
            }
            // A card detail view is open on top of us. Re-rendering underneath
            // would yank content out from under the user, so skip this cycle
            // and try again later if there is budget left.
            if (isDetailViewOpen()) {
                scheduleSilentRefresh()
                return@setTimeout
            }
            loadPersonalizedCards(isAutoRefresh = true)
        }, SILENT_REFRESH_DELAY_MS)
    }

    private fun cancelPendingRefresh() {
        pendingRefreshTimer?.let { window.clearTimeout(it) }
        pendingRefreshTimer = null
    }

    private fun isDetailViewOpen(): Boolean =
        navigationManager?.getCurrentScreen() is Screen.Wiz3RCardDetail ||
            document.getElementById("guide-modal-overlay") != null

    /**
     * Same render as [updateUI], but keeps the carousel where the user left it
     * — innerHTML replacement would otherwise snap it back to the first card.
     */
    private fun updateUIPreservingScroll() {
        val element = container ?: return
        val scrollLeft = (element.querySelector(".hide-scrollbar") as? HTMLElement)?.scrollLeft
        updateUI()
        if (scrollLeft != null) {
            (element.querySelector(".hide-scrollbar") as? HTMLElement)?.scrollLeft = scrollLeft
        }
    }

    private fun updateUI() {
        val element = container ?: return
        val t = WebTheme.current

        // Mark the container when at least one card has a Wiz 3R narrative so
        // the contextual guide can swap to the 3R-aware tour body.
        val anyNarrative = cards.any { it.narrative != null }
        element.setAttribute("data-wiz3r-active", if (anyNarrative) "true" else "false")

        element.innerHTML = """
            <div style="margin-top: 16px;">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
                    <h3 style="color: ${t.textPrimary}; margin: 0; font-size: 16px; font-weight: 600;">
                        ${StringKey.ADVICE_PERSONALIZED_INSIGHTS.tr()}
                    </h3>
                    ${if (userDataDays > 0) """
                        <span style="color: ${t.textSecondary}; font-size: 11px;">
                            ${StringKey.ADVICE_BASED_ON_DAYS.tr(userDataDays.toString())}
                        </span>
                    """ else ""}
                </div>
                ${renderContent()}
            </div>
        """

        setupEventListeners()
    }

    private fun renderContent(): String {
        val t = WebTheme.current
        if (isLoading) {
            return """
                <div style="
                    background: ${t.card};
                    border-radius: 12px;
                    padding: 32px;
                    text-align: center;
                    border: 1px solid ${t.border};
                ">
                    <div style="color: ${t.textSecondary}; font-size: 14px;">
                        ${StringKey.ADVICE_ANALYZING_PATTERNS.tr()}
                    </div>
                </div>
            """
        }

        if (error != null) {
            return """
                <div style="
                    background: ${t.card};
                    border-radius: 12px;
                    padding: 20px;
                    text-align: center;
                    border: 1px solid ${t.border};
                ">
                    <div style="color: ${t.expenseRed}; font-size: 14px;">
                        $error
                    </div>
                </div>
            """
        }

        if (cards.isEmpty()) {
            return """
                <div style="
                    background: ${t.card};
                    border-radius: 12px;
                    padding: 32px;
                    text-align: center;
                    border: 1px solid ${t.border};
                ">
                    <div style="font-size: 32px; margin-bottom: 12px;">
                        📊
                    </div>
                    <div style="color: ${t.textSecondary}; font-size: 14px;">
                        ${StringKey.ADVICE_NO_ADVICE_YET.tr()}
                    </div>
                    <div style="color: ${t.textSecondary}; font-size: 12px; margin-top: 8px;">
                        ${StringKey.ADVICE_KEEP_TRACKING.tr()}
                    </div>
                </div>
            """
        }

        // Both mobile and desktop now use the same horizontal scroll-snap
        // carousel — only the per-card width differs, which controls how
        // many cards are visible at a time:
        //   - touch (phone, tablet) → 1 card centred (~84% width), neighbours peek
        //   - desktop                → 3 cards visible (~30% each), 4th+ peeks right
        //
        // Pointer capability is the right signal here, not viewport width:
        // a desktop user resizing their window narrow should still get the
        // 3-up layout (just smaller cards), not flip to the 1-up touch
        // carousel. matchMedia "(hover: none) and (pointer: coarse)" is
        // true only on real touch devices.
        val isTouch = window.matchMedia("(hover: none) and (pointer: coarse)").matches
        val cardHtmls = cards.mapIndexed { index, card -> renderCard(card, index, isTouch) }
        return """
            <div style="
                display: flex;
                gap: 12px;
                overflow-x: auto;
                scroll-snap-type: x mandatory;
                scroll-padding-left: ${if (isTouch) "8%" else "24px"};
                scroll-padding-right: ${if (isTouch) "8%" else "24px"};
                scrollbar-width: none;
                -ms-overflow-style: none;
                padding: 4px 0 12px 0;
            " class="hide-scrollbar">
                ${cardHtmls.joinToString("")}
            </div>
            <style>.hide-scrollbar::-webkit-scrollbar { display: none; }</style>
        """
    }

    private fun renderCard(card: PersonalizedCardData, index: Int, isTouch: Boolean = false): String {
        val t = WebTheme.current
        // Both layouts use the same scroll-snap carousel; only the per-card
        // width differs. Touch: one centred card (~84% viewport width).
        // Desktop: three visible at a time (~30% each) with the next one
        // peeking on the right edge. The min-width 280px clamp on desktop
        // keeps cards readable when the user resizes their window narrow.
        val carouselSizing = if (isTouch) {
            "flex: 0 0 84%; scroll-snap-align: center; scroll-snap-stop: always;"
        } else {
            // min-width 440px so the 3-section REVEAL / REASON / REALIZE
            // stack stays readable when the viewport narrows. Previously
            // 280px let the calc() shrink cards to ~300px on 1080-wide
            // portrait windows (marketing capture, split-screen browsing),
            // which made every paragraph in the personalized-insights
            // section wrap at ~40 chars — pinched and hard to read. The
            // carousel is already horizontal-scroll so trading "3 visible
            // when it doesn't fit" for "2 visible + peek" is a strict
            // upgrade — the user scrolls right the same way they always
            // would.
            //
            // On 1920px+ the calc() (~553px per card) still wins over
            // this min-width, so wider desktops keep the 3-up layout.
            "flex: 0 0 calc((100% - 96px) / 3.3); min-width: 440px; scroll-snap-align: start; scroll-snap-stop: always;"
        }
        val bodyHtml = card.bodyLines.joinToString("<br>") { line ->
            "<span style=\"color: ${t.textSecondary}; font-size: 13px; line-height: 1.5;\">$line</span>"
        }

        // Wiz 3R AI enhancement — only renders when the server attached one.
        // The card carries Reveal alone: it's the observation, and that's the
        // hook. Reason (why) and Realize (what to do) are the payoff and live
        // in the detail view. Printing all three here left the detail with
        // nothing new to show — it repeats the same prose, then adds the math,
        // chart and actions — so there was no reason to open it.
        //
        // No section label either: with one section there's nothing to
        // contrast it against, so "REVEAL" reads as jargon. The detail keeps
        // the labels, where all three appear and the framework is legible.
        val narrativeHtml = card.narrative?.let { n ->
            """
                <div style="
                    margin-top: 12px;
                    margin-left: 48px;
                    padding: 6px 2px 6px 8px;
                    border-left: 2px solid ${card.iconColor};
                    background: ${card.iconColor}0a;
                    border-radius: 0 6px 6px 0;
                ">
                    <div
                        class="wiz3r-section"
                        data-card-index="$index"
                        data-section-anchor="reveal"
                        style="
                            padding: 8px 28px 8px 10px;
                            border-radius: 4px;
                            cursor: pointer;
                            transition: background 0.15s;
                            position: relative;
                            -webkit-user-select: none;
                            user-select: none;
                            -webkit-touch-callout: none;
                        "
                        onmouseover="this.style.background='${card.iconColor}1a'"
                        onmouseout="this.style.background='transparent'"
                    >
                        <div style="font-size: 12px; color: ${t.textSecondary}; line-height: 1.5;">${n.reveal}</div>
                        <span style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: ${card.iconColor}; font-size: 13px; opacity: 0.55;">→</span>
                    </div>
                </div>
            """.trimIndent()
        } ?: ""

        val chartHtml = card.chart?.let { c ->
            """
                <div style="margin-top: 10px; margin-left: 48px; padding: 8px; background: rgba(0,0,0,0.18); border-radius: 6px;">
                    ${Wiz3RChart.renderToSvg(c)}
                </div>
            """.trimIndent()
        } ?: ""

        // The card's doorway into the detail view, where Reason and Realize
        // live. It names the reasoning rather than the brand ("see why") so it
        // promises what's actually behind the tap, and mentions asking because
        // the chat panel there is otherwise easy to miss.
        //
        // Standard users (revealTeaser) get the same button, PRO-locked: the
        // Reveal is on the card, but the reasoning + chat are the upsell, so the
        // button carries a PRO pill and routes to the paywall (class wiz3r-ask-locked).
        val askWiz3rHtml = when {
            card.canDrillDown -> """
                <div style="margin-top: 10px; margin-left: 48px;">
                    <button
                        class="wiz3r-ask"
                        data-card-index="$index"
                        style="
                            padding: 6px 10px;
                            background: transparent;
                            border: 1px solid ${card.iconColor}66;
                            color: ${card.iconColor};
                            border-radius: 6px;
                            font-size: 11px;
                            cursor: pointer;
                            transition: background 0.15s;
                        "
                        onmouseover="this.style.background='${card.iconColor}1a'"
                        onmouseout="this.style.background='transparent'"
                    >${StringKey.WIZ3R_CARD_SEE_WHY.tr()}</button>
                </div>
            """.trimIndent()
            // Identical to the Pro button above, plus the PRO pill — a standard
            // user should see the same card, same affordance, just locked.
            card.revealTeaser -> """
                <div style="margin-top: 10px; margin-left: 48px;">
                    <button
                        class="wiz3r-ask-locked"
                        data-card-index="$index"
                        style="
                            display: inline-flex; align-items: center; gap: 6px;
                            padding: 6px 10px;
                            background: transparent;
                            border: 1px solid ${card.iconColor}66;
                            color: ${card.iconColor};
                            border-radius: 6px;
                            font-size: 11px;
                            cursor: pointer;
                            transition: background 0.15s;
                        "
                        onmouseover="this.style.background='${card.iconColor}1a'"
                        onmouseout="this.style.background='transparent'"
                    >${StringKey.WIZ3R_CARD_SEE_WHY.tr()}<span style="background: ${WebTheme.current.proBadge}; color: ${WebTheme.current.proBadgeText}; font-size: 8px; font-weight: 700; padding: 1px 4px; border-radius: 3px; letter-spacing: 0.3px;">PRO</span></button>
                </div>
            """.trimIndent()
            else -> ""
        }

        // Hide the bottom action button when the narrative is present — the
        // Reveal section and the "see why" button above carry the same action.
        // It's one or the other, never both: a card either offers "See why — and
        // ask" (Pro, or Pro-locked for standard) or its own plain action button.
        // Pro already hides this when a narrative is present; a teaser card must
        // hide it too, or standard users get two stacked CTAs.
        val actionHtml = if (card.actionText != null && card.actionType != "NONE" && card.narrative == null && !card.revealTeaser) {
            """
                <button
                    class="advice-card-action"
                    data-card-index="$index"
                    data-action-type="${card.actionType}"
                    data-action-category="${card.actionData["category"] ?: ""}"
                    data-action-goal-id="${card.actionData["goalId"] ?: ""}"
                    data-action-merchants="${card.actionData["merchants"] ?: ""}"
                    data-action-title="${card.title}"
                    style="
                        padding: 10px 16px;
                        background: transparent;
                        border: 1px solid ${card.iconColor};
                        border-radius: 6px;
                        color: ${card.iconColor};
                        cursor: pointer;
                        font-size: 12px;
                        font-weight: 500;
                        width: 66%;
                        text-align: center;
                        transition: all 0.2s;
                    "
                    onmouseover="this.style.background='${card.iconColor}20'"
                    onmouseout="this.style.background='transparent'"
                >
                    ${card.actionText}
                </button>
            """
        } else ""

        return """
            <div class="advice-card" data-card-id="${card.id}" style="
                background: ${t.card};
                border-radius: 12px;
                padding: 16px;
                border: 1px solid ${t.border};
                box-shadow: ${t.cardShadow};
                position: relative;
                $carouselSizing
                transition: all 0.2s;
                display: flex;
                flex-direction: column;
            ">
                <!-- Dismiss button -->
                <button
                    class="advice-card-dismiss"
                    data-card-id="${card.id}"
                    style="
                        position: absolute;
                        top: 12px;
                        right: 12px;
                        width: 20px;
                        height: 20px;
                        border: none;
                        background: transparent;
                        color: ${t.textSecondary};
                        cursor: pointer;
                        font-size: 16px;
                        line-height: 1;
                        padding: 0;
                        opacity: 0.5;
                        transition: opacity 0.2s;
                    "
                    onmouseover="this.style.opacity='1'"
                    onmouseout="this.style.opacity='0.5'"
                    title="${StringKey.ADVICE_DISMISS.tr()}"
                >&times;</button>

                <!-- Header with icon and title -->
                <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 10px; padding-right: 24px;">
                    <div style="
                        width: 36px;
                        height: 36px;
                        border-radius: 8px;
                        background: ${card.iconColor}15;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                    ">
                        ${getIconEmoji(card.icon, card.iconColor)}
                    </div>
                    <h4 style="
                        color: ${t.textPrimary};
                        margin: 0;
                        font-size: 14px;
                        font-weight: 600;
                    ">${card.title}</h4>
                </div>

                <!-- Body content — hidden when an LLM narrative is present;
                     the Reveal section carries the same data and re-stating
                     it above is redundant noise. -->
                ${if (card.narrative == null) """
                <div style="padding-left: 48px; flex: 1;">
                    $bodyHtml
                </div>
                """ else ""}

                $narrativeHtml
                $chartHtml

                <!-- Action button centered on full card width. It comes BEFORE the
                     Pro upsell: on a card that still has its own action ("See X
                     transactions"), that action is what the user came for, and the
                     locked "See why" is a secondary nudge that shouldn't outrank it. -->
                <div style="display: flex; justify-content: center; margin-top: 12px;">
                    $actionHtml
                </div>

                $askWiz3rHtml
            </div>
        """
    }

    private fun getIconEmoji(iconName: String, color: String): String {
        // Map icon names to emoji for simplicity (could use SVG icons later)
        val emoji = when (iconName) {
            "warning" -> "⚠️"
            "trending_up" -> "📈"
            "trending_down" -> "📉"
            "star" -> "⭐"
            "thumb_up" -> "👍"
            "check_circle" -> "✅"
            "schedule" -> "⏰"
            "emoji_events" -> "🏆"
            "celebration" -> "🎉"
            "weekend" -> "🌴"
            "subscriptions" -> "📱"
            "lightbulb" -> "💡"
            "waving_hand" -> "👋"
            "flag" -> "🚀"
            else -> "💎"
        }
        return """<span style="font-size: 18px;">$emoji</span>"""
    }

    private fun setupEventListeners() {
        val element = container ?: return

        // Action buttons
        val actionButtons = element.querySelectorAll(".advice-card-action")
        for (i in 0 until actionButtons.length) {
            val button = actionButtons[i] as? HTMLElement ?: continue
            button.onclick = { event ->
                event.preventDefault()
                val actionType = button.getAttribute("data-action-type") ?: ""
                val category = button.getAttribute("data-action-category") ?: ""
                val goalId = button.getAttribute("data-action-goal-id") ?: ""
                val cardIndex = button.getAttribute("data-card-index")?.toIntOrNull() ?: -1
                val merchants = button.getAttribute("data-action-merchants") ?: ""
                val title = button.getAttribute("data-action-title") ?: ""

                handleCardAction(actionType, category, goalId, cardIndex, merchants, title)
            }
        }

        // Dismiss buttons
        val dismissButtons = element.querySelectorAll(".advice-card-dismiss")
        for (i in 0 until dismissButtons.length) {
            val button = dismissButtons[i] as? HTMLElement ?: continue
            button.onclick = { event ->
                event.preventDefault()
                event.stopPropagation()
                val cardId = button.getAttribute("data-card-id") ?: ""
                dismissCard(cardId)
            }
        }

        // Wiz 3R section taps — navigate to the card detail page with the
        // tapped anchor (the card shows Reveal only; the handler stays generic
        // because the detail still anchors to any of the three). The payload is
        // stashed on the nav manager so the detail component can read it
        // without re-fetching.
        val sections = element.querySelectorAll(".wiz3r-section")
        for (i in 0 until sections.length) {
            val sec = sections[i] as? HTMLElement ?: continue
            sec.onclick = { event ->
                event.preventDefault()
                event.stopPropagation()
                val cardIndex = sec.getAttribute("data-card-index")?.toIntOrNull() ?: -1
                val anchor = sec.getAttribute("data-section-anchor")
                val card = cards.getOrNull(cardIndex)
                if (card != null && navigationManager != null) {
                    if (card.revealTeaser) {
                        // Standard user: the Reveal is all they get for free —
                        // tapping through is the upsell.
                        navigationManager.navigateTo(Screen.Subscribe())
                    } else {
                        navigationManager.setSelectedWiz3rCard(card)
                        navigationManager.navigateTo(Screen.Wiz3RCardDetail(card.id, anchor))
                    }
                }
            }
        }

        // Pro-locked "See why" on a teaser card → straight to the paywall.
        val lockedAskButtons = element.querySelectorAll(".wiz3r-ask-locked")
        for (i in 0 until lockedAskButtons.length) {
            val btn = lockedAskButtons[i] as? HTMLElement ?: continue
            btn.onclick = { event ->
                event.preventDefault()
                event.stopPropagation()
                navigationManager?.navigateTo(Screen.Subscribe())
            }
        }

        // "Ask Wiz 3R" button — opens the detail page with the chat panel
        // already open (saves the user a click).
        val askButtons = element.querySelectorAll(".wiz3r-ask")
        for (i in 0 until askButtons.length) {
            val btn = askButtons[i] as? HTMLElement ?: continue
            btn.onclick = { event ->
                event.preventDefault()
                event.stopPropagation()
                val cardIndex = btn.getAttribute("data-card-index")?.toIntOrNull() ?: -1
                val card = cards.getOrNull(cardIndex)
                if (card != null && navigationManager != null) {
                    navigationManager.setSelectedWiz3rCard(card)
                    navigationManager.navigateTo(
                        Screen.Wiz3RCardDetail(card.id, anchor = null, openChat = true),
                    )
                }
            }
        }
    }

    private fun handleCardAction(actionType: String, category: String, goalId: String, cardIndex: Int = -1, merchants: String = "", title: String = "") {
        when (actionType) {
            "NAVIGATE_CATEGORY" -> {
                val merchantList = merchants.split(",").map { it.trim() }.filter { it.isNotEmpty() }
                if (onShowFilteredTransactions != null) {
                    onShowFilteredTransactions.invoke(category.ifEmpty { null }, merchantList, title)
                } else {
                    navigationManager?.navigateTo(Screen.TransactionList(categoryFilter = category.ifEmpty { null }))
                }
            }
            "NAVIGATE_GOAL" -> {
                // Scroll to the specific goal card and highlight it
                val selector = if (goalId.isNotEmpty()) ".goalCard[data-goal-id=\"$goalId\"]" else null
                val goalElement = selector?.let { document.querySelector(it) as? HTMLElement }
                if (goalElement != null) {
                    goalElement.scrollIntoView(js("{behavior: 'smooth', block: 'center'}"))
                    goalElement.style.outline = "2px solid #4caf50"
                    goalElement.style.outlineOffset = "2px"
                    window.setTimeout({
                        goalElement.style.outline = ""
                        goalElement.style.outlineOffset = ""
                    }, 2000)
                }
            }
            "NAVIGATE_TRANSACTIONS" -> {
                if (onShowFilteredTransactions != null) {
                    onShowFilteredTransactions.invoke(null, emptyList(), title)
                } else {
                    navigationManager?.navigateTo(Screen.TransactionList())
                }
            }
            "CREATE_GOAL" -> {
                navigationManager?.navigateTo(Screen.SavingsGoals(openCreateDialog = true))
            }
            "NAVIGATE_GUIDE" -> {
                showGuideModal()
            }
            "REVIEW_SUBSCRIPTIONS" -> {
                onReviewSubscriptions?.invoke()
            }
            "SHARE" -> {
                val card = if (cardIndex >= 0) cards.getOrNull(cardIndex) else null
                val shareText = card?.let { c ->
                    toShareText(c.title, c.bodyLines)
                } ?: "Check out my financial milestone on wiz\u2261r! See https://wiz3r.com"

                val navigator = js("window.navigator")
                if (js("typeof navigator.share === 'function'") as Boolean) {
                    val shareData = js("{}")
                    shareData.title = "wiz\u2261r"
                    shareData.text = shareText
                    navigator.share(shareData)
                } else {
                    kotlinx.browser.window.navigator.clipboard.writeText(shareText)
                }
            }
        }
    }

    private fun toShareText(title: String, bodyLines: List<String>): String {
        val lines = mutableListOf(title)
        for (line in bodyLines) {
            // Transform achievement lines to first person
            var transformed = line
                // English
                .replace("Your ", "My ").replace("your ", "my ")
                // French
                .replace("Votre ", "Mon ").replace("votre ", "mon ")

            // Drop advice/coaching lines that don't make sense when shared
            if (transformed.startsWith("Keep this up", ignoreCase = true) ||
                transformed.startsWith("Continuez", ignoreCase = true)) {
                // Rewrite: "Keep this up and you'll have..." → "That could mean..."
                val amountMatch = Regex("\\$[\\d,]+").find(transformed)
                if (amountMatch != null) {
                    transformed = "That could mean an extra ${amountMatch.value} available this year."
                } else {
                    continue // skip line entirely if we can't extract useful info
                }
            }

            lines.add(transformed)
        }
        lines.add("Thanks to wiz\u2261r! See https://wiz3r.com")
        return lines.joinToString("\n")
    }

    private fun dismissCard(cardId: String) {
        // Remove card from UI immediately
        cards = cards.filter { it.id != cardId }
        updateUI()

        // Persist dismissal to server (7-day temporary dismissal)
        GlobalScope.launch {
            try {
                val token = ApiService.getSharedAuthToken() ?: return@launch
                val baseUrl = getBaseUrl()

                window.fetch(
                    "$baseUrl/api/advice/personalized/$cardId/dismiss",
                    RequestInit(
                        method = "POST",
                        headers = json(
                            "Authorization" to "Bearer $token",
                            "Content-Type" to "application/json"
                        )
                    )
                ).await()
            } catch (e: Exception) {
                console.warn("Failed to persist card dismissal: ${e.message}")
            }
        }
    }

    private fun getBaseUrl(): String {
        val hostname = window.location.hostname
        return if (hostname == "localhost" || hostname == "127.0.0.1") {
            "http://localhost:3000"
        } else {
            ""
        }
    }

    // Refresh cards (can be called externally)
    fun refresh() {
        loadPersonalizedCards()
    }

    companion object {
        private const val SILENT_REFRESH_DELAY_MS = 12000
        private const val MAX_SILENT_REFRESH_RETRIES = 3
    }
}

// Data classes for API response parsing

@Serializable
data class PersonalizedCardsApiResponse(
    val cards: List<PersonalizedCardData>,
    val generatedAt: String,
    val userDataDays: Int = 0,
    // True when the server answered from an older cached bundle and kicked off
    // a background regeneration; the client re-fetches shortly after.
    val refreshing: Boolean = false
)

@Serializable
data class PersonalizedCardData(
    val id: String,
    val type: String,
    val priority: Int,
    val title: String,
    val icon: String,
    val iconColor: String,
    val bodyLines: List<String>,
    val actionText: String? = null,
    val actionType: String = "NONE",
    val actionData: Map<String, String> = emptyMap(),
    val metrics: CardMetricsData? = null,
    // Wiz 3R AI fields — populated by Wiz3RPersonalizer on the server for
    // users with the AI flag enabled. Null for everyone else; the card
    // renders identically to before in that case.
    val narrative: ai.ReasonedNarrative? = null,
    val chart: ai.ChartSpec? = null,
    val canDrillDown: Boolean = false,
    // Standard-tier teaser: Reveal is present, but Reason/Realize/chat are
    // Pro-locked. The "See why" button carries a PRO pill and opens the paywall.
    val revealTeaser: Boolean = false,
)

@Serializable
data class CardMetricsData(
    val amount: Double? = null,
    val percentOfIncome: Double? = null,
    val percentChange: Double? = null,
    val previousAmount: Double? = null,
    val savingsRate: Double? = null,
    val previousSavingsRate: Double? = null,
    val goalProgress: Double? = null,
    val targetDate: String? = null,
    val projectedDate: String? = null,
    val category: String? = null,
    val percentOfBudget: Double? = null,
    val plannedAmount: Double? = null,
)
